import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, useRouteError } from "react-router-dom";
import ProtectedRoute from "./Components/ProtectedRoute/ProtectedRoute";
import PlanProtectedRoute from "./Components/PlanProtectedRoute/PlanProtectedRoute";
import SAProtectedRoute from "./Components/SAProtectedRoute/SAProtectedRoute";
import Layout from "./Components/Layout/Layout";
import Login from "./Components/Login/Login";
import App from "./App";
import LandingPage from "./pages/Landing/LandingPage";
import PricingPage from "./Components/HomePage/Pricing/PricingPage";
import Privacy from "./Components/HomePage/Privacy/Privacy";
import Refund from "./Components/HomePage/Refund/Refund";
import Terms from "./Components/HomePage/Term&Service/Terms";
import SuperAdminLogin from "./pages/SuperAdminLogin/SuperAdminLogin";
import ChangePassword from "./pages/ChangePassword/ChangePassword";

const Home = lazy(() => import('./pages/home/home'));
const Customers = lazy(() => import('./Components/Customer/Customer'));
const Vendors = lazy(() => import('./Components/Vendor/Vendor'));
const Invoice = lazy(() => import('./Components/Invoice/Invoice'));
const Payment = lazy(() => import('./Components/Payment/Payment'));
const NewInvoice = lazy(() => import('./Components/Invoice/NewInvoice'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings/ProfileSettings'));
const AppSettings = lazy(() => import('./pages/AppSettings/AppSettings'));
const ShowInvoice = lazy(() => import('./Components/Invoice/ShowInvoice'));
const RecordPayment = lazy(() => import('./Components/Payment/RecordPayment'));
const ShowPayment = lazy(() => import('./Components/Payment/ShowPayment'));
const Ledger = lazy(() => import('./Components/Ledger/Ledger'));
const UserGuide = lazy(() => import('./pages/guides/UserGuide'));
const OpeningBalances = lazy(() => import('./pages/setup/OpeningBalances'));
// Sales Module Pages
const QuotesList = lazy(() => import('./pages/sales/quotes/QuotesList'));
const QuoteForm = lazy(() => import('./pages/sales/quotes/QuoteForm'));
const InvoicesList = lazy(() => import('./pages/sales/invoices/InvoicesList'));
const InvoiceForm = lazy(() => import('./pages/sales/invoices/InvoiceForm'));
const ShowSaleInvoice = lazy(() => import('./pages/sales/show/ShowSaleInvoice'));
// Import the new Sales components
const OrdersList = lazy(() => import('./pages/sales/orders/OrdersList'));
const OrderForm = lazy(() => import('./pages/sales/orders/OrderForm'));
// Legacy /sales/credits mocks removed 2026-09-14 — replaced by
// /accounts/credit-notes (real backend). Sidebar link retargeted.
// Import the Sales Reports component
const SalesReports = lazy(() => import('./pages/sales/reports/SalesReports'));
const SecondarySales = lazy(() => import('./pages/sales/secondary/SecondarySales'));
const DistributorStock = lazy(() => import('./pages/sales/distributorstock/DistributorStock'));
// Import the Purchase components
const PurchaseDashboard = lazy(() => import('./pages/purchases/dashboard/PurchaseDashboard'));
const SalesDashboard = lazy(() => import('./pages/sales/dashboard/SalesDashboard'));
const FinanceDashboard = lazy(() => import('./pages/finance/dashboard/FinanceDashboard'));
const PurchaseOrdersList = lazy(() => import('./pages/purchases/orders/PurchaseOrdersList'));
const PurchaseOrderForm = lazy(() => import('./pages/purchases/orders/PurchaseOrderForm'));
// Import other Purchase module components
const PurchaseBillsList = lazy(() => import('./pages/purchases/bills/PurchaseBillsList'));
const PurchaseBillForm = lazy(() => import('./pages/purchases/bills/PurchaseBillForm'));
const PurchaseReturnsList = lazy(() => import('./pages/purchases/returns/PurchaseReturnsList'));
const PurchaseReturnForm = lazy(() => import('./pages/purchases/returns/PurchaseReturnForm'));
const PurchasesReports = lazy(() => import('./pages/purchases/reports/PurchasesReports'));
const PurchaseAnalytics = lazy(() => import('./pages/purchases/analytics/PurchaseAnalytics'));
// Import new purchase components
const RequisitionsList = lazy(() => import('./pages/purchases/requisitions/RequisitionsList'));
const RequisitionForm = lazy(() => import('./pages/purchases/requisitions/RequisitionForm'));
const VendorQuotesList = lazy(() => import('./pages/purchases/quotes/VendorQuotesList'));
const VendorQuoteForm = lazy(() => import('./pages/purchases/quotes/VendorQuoteForm'));
// Import PO and GRN components
const POList = lazy(() => import('./pages/purchases/po').then(m => ({ default: m.POList })));
const POForm = lazy(() => import('./pages/purchases/po').then(m => ({ default: m.POForm })));
const GRNList = lazy(() => import('./pages/purchases/grn').then(m => ({ default: m.GRNList })));
const GRNForm = lazy(() => import('./pages/purchases/grn').then(m => ({ default: m.GRNForm })));
// Product components now handled by unified inventory system

// Import Service components
const ServiceList = lazy(() => import('./pages/services/ServiceList'));
const ServiceForm = lazy(() => import('./pages/services/ServiceForm'));
// Import Category components
const CategoriesRoutes = lazy(() => import('./pages/categories'));
const CategoryList = lazy(() => import('./pages/categories/list/CategoryList'));
const CategoryForm = lazy(() => import('./pages/categories/form/CategoryForm'));
// Import Inventory components
const InventoryList = lazy(() => import('./pages/inventory/list/InventoryList'));
const InventoryForm = lazy(() => import('./pages/inventory/form/InventoryForm'));
const ShowInventory = lazy(() => import('./pages/inventory/show/ShowInventory'));
const InventoryAdjustments = lazy(() => import('./pages/inventory/adjustments/InventoryAdjustments'));
const InventoryAdjustmentForm = lazy(() => import('./pages/inventory/adjustments/InventoryAdjustmentForm'));
const InventoryTransfers = lazy(() => import('./pages/inventory/transfers/InventoryTransfers'));
const InventoryTransferForm = lazy(() => import('./pages/inventory/transfers/InventoryTransferForm'));
const InventoryDashboard = lazy(() => import('./pages/inventory/dashboard/InventoryDashboard'));
const InventoryValuation = lazy(() => import('./pages/inventory/valuations/InventoryValuation'));
const InventoryReports = lazy(() => import('./pages/inventory/reports/InventoryReports'));
// Import Unit of Measure components
const UnitOfMeasureList = lazy(() => import('./pages/settings/units/UnitOfMeasureList'));
const UnitOfMeasureForm = lazy(() => import('./pages/settings/units/UnitOfMeasureForm'));
// Import Reports components
const ReportsDashboard = lazy(() => import('./pages/reports').then(m => ({ default: m.ReportsDashboard })));
const ProfitLossReport = lazy(() => import('./pages/reports').then(m => ({ default: m.ProfitLossReport })));
const BalanceSheet = lazy(() => import('./pages/reports').then(m => ({ default: m.BalanceSheet })));
const TaxReport = lazy(() => import('./pages/reports').then(m => ({ default: m.TaxReport })));
const CustomerAgingReport = lazy(() => import('./pages/reports').then(m => ({ default: m.CustomerAgingReport })));
const ChartOfAccountsReport = lazy(() => import('./pages/reports').then(m => ({ default: m.ChartOfAccounts })));
const TrialBalance = lazy(() => import('./pages/reports').then(m => ({ default: m.TrialBalance })));
const Postings = lazy(() => import('./pages/reports').then(m => ({ default: m.Postings })));
const CorporateTax = lazy(() => import('./pages/reports').then(m => ({ default: m.CorporateTax })));
const VAT = lazy(() => import('./pages/reports').then(m => ({ default: m.VAT })));
const Cashflow = lazy(() => import('./pages/reports').then(m => ({ default: m.Cashflow })));
const AgedCreditors = lazy(() => import('./pages/reports').then(m => ({ default: m.AgedCreditors })));
// Import Admin components
const UserManagement = lazy(() => import('./pages/admin').then(m => ({ default: m.UserManagement })));
const BranchManagement = lazy(() => import('./pages/admin').then(m => ({ default: m.BranchManagement })));
const DepartmentManagement = lazy(() => import('./pages/admin').then(m => ({ default: m.DepartmentManagement })));
const RoleManagement = lazy(() => import('./pages/admin').then(m => ({ default: m.RoleManagement })));
const UserRoles = lazy(() => import('./pages/admin').then(m => ({ default: m.UserRoles })));
const StatusDashboard = lazy(() => import('./pages/admin').then(m => ({ default: m.StatusDashboard })));
const UserPermissions = lazy(() => import('./pages/admin/UserPermissions'));
const AuthorityManagement = lazy(() => import('./pages/admin/authority/AuthorityManagement'));
const ApprovalPage = lazy(() => import('./pages/purchases/requisitions/ApprovalPage'));
const SuperAdmin = lazy(() => import('./pages/superadmin/SuperAdmin'));
const SupportTickets = lazy(() => import('./pages/support/SupportTickets'));
const Messages = lazy(() => import('./pages/messages/Messages'));
const Maintenance = lazy(() => import('./pages/Maintenance/Maintenance'));
const CustomRoles = lazy(() => import('./pages/admin/roles/CustomRoles'));
const ChartOfAccounts = lazy(() => import('./pages/accounts/ChartOfAccounts'));
const JournalVouchers = lazy(() => import('./pages/accounts/journal/JournalVouchers'));
const FinancialAccounts = lazy(() => import('./pages/accounts/financial/FinancialAccounts'));
const FiscalYears = lazy(() => import('./pages/accounts/fiscal/FiscalYears'));
const CashBook = lazy(() => import('./pages/accounts/cashbook/CashBook'));
const BankReconciliation = lazy(() => import('./pages/accounts/reconciliation/BankReconciliation'));
const ControlCenter = lazy(() => import('./pages/accounts/controlCenter/ControlCenter'));
const TaxCodes = lazy(() => import('./pages/settings/taxCodes/TaxCodes'));
const HrOperations = lazy(() => import('./pages/hr/operations/HrOperations'));
const AdvanceEditor = lazy(() => import('./pages/hr/advances/AdvanceEditor'));
const SettlementEditor = lazy(() => import('./pages/hr/settlements/SettlementEditor'));
const CreditNotes = lazy(() => import('./pages/accounts/creditNotes/CreditNotes'));
const BankTransfer = lazy(() => import('./pages/accounts/bankTransfer/BankTransfer'));
const FaReconciliation = lazy(() => import('./pages/assets/reconciliation/FaReconciliation'));
const PeriodCloseWizard = lazy(() => import('./pages/accounts/periodClose/PeriodCloseWizard'));
const AssetLifecycle = lazy(() => import('./pages/assets/lifecycle/AssetLifecycle'));
const MrpRuns = lazy(() => import('./pages/production/mrp/MrpRuns'));
const QualityChecks = lazy(() => import('./pages/production/quality/QualityChecks'));
const AuditLogsPage = lazy(() => import('./pages/admin/auditLogs/AuditLogs'));
const SecurityCenter = lazy(() => import('./pages/admin/security/SecurityCenter'));
const DocumentLifecycle = lazy(() => import('./pages/accounts/lifecycle/DocumentLifecycle'));
const TrialBalanceView = lazy(() => import('./pages/reports/professional/TrialBalanceView'));
const ProfitLossView = lazy(() => import('./pages/reports/professional/ProfitLossView'));
const BalanceSheetView = lazy(() => import('./pages/reports/professional/BalanceSheetView'));
const CashflowView = lazy(() => import('./pages/reports/professional/CashflowView'));
const CustomerAgingView = lazy(() => import('./pages/reports/professional/CustomerAgingView'));
const AgedCreditorsView = lazy(() => import('./pages/reports/professional/AgedCreditorsView'));
const VatView = lazy(() => import('./pages/reports/professional/VatView'));
const CorporateTaxView = lazy(() => import('./pages/reports/professional/CorporateTaxView'));
const PayrollByDepartmentView = lazy(() => import('./pages/reports/professional/PayrollByDepartmentView'));
const ExpenseByCategoryView = lazy(() => import('./pages/reports/professional/ExpenseByCategoryView'));
const AssetTagging = lazy(() => import('./pages/assets/tagging/AssetTagging'));
const ProductionVariance = lazy(() => import('./pages/production/variance/ProductionVariance'));
const Payslip = lazy(() => import('./pages/hr/payslip/Payslip'));
const TwoFactorSetup = lazy(() => import('./pages/settings/twoFactor/TwoFactorSetup'));
const InventoryFifoValuation = lazy(() => import('./pages/inventory/valuation/InventoryValuation'));
const PettyCash = lazy(() => import('./pages/accounts/pettycash/PettyCash'));
const CustomerLedger = lazy(() => import('./pages/customers/CustomerLedger'));
const VendorLedger = lazy(() => import('./pages/vendors/VendorLedger'));
const Collections = lazy(() => import('./pages/collections/Collections'));
const ExpenseBills = lazy(() => import('./pages/expensebills/ExpenseBills'));
const ApReconciliation = lazy(() => import('./pages/reports/ap/ApReconciliation'));
const WithholdingTaxes = lazy(() => import('./pages/settings/wht/WithholdingTaxes'));
const PayrollTaxSlabs = lazy(() => import('./pages/settings/payrolltax/PayrollTaxSlabs'));
const ExchangeRates = lazy(() => import('./pages/settings/fx/ExchangeRates'));
const PostingRules = lazy(() => import('./pages/settings/postingrules/PostingRules'));
const CostCenters = lazy(() => import('./pages/settings/costcenters/CostCenters'));
const FixedAssets = lazy(() => import('./pages/assets/FixedAssets'));
const Depreciation = lazy(() => import('./pages/assets/Depreciation'));
const JVNumberingRules = lazy(() => import('./pages/settings/jvnumbering/JVNumberingRules'));
const ExpenseReimbursements = lazy(() => import('./pages/reimbursements/ExpenseReimbursements'));
const InterCompanyTransactions = lazy(() => import('./pages/intercompany/InterCompanyTransactions'));
const Accruals = lazy(() => import('./pages/accruals/Accruals'));
const Employees = lazy(() => import('./pages/hr/Employees'));
const LettersOfCredit = lazy(() => import('./pages/imports/LettersOfCredit'));
const Shipments = lazy(() => import('./pages/imports/Shipments'));
const CustomsEntries = lazy(() => import('./pages/imports/CustomsEntries'));
const LandedCosts = lazy(() => import('./pages/imports/LandedCosts'));
const LcSettlements = lazy(() => import('./pages/imports/LcSettlements'));
const ImportCostAnalytics = lazy(() => import('./pages/imports/ImportCostAnalytics'));
const BillOfMaterials = lazy(() => import('./pages/production/BillOfMaterials'));
const WorkOrders = lazy(() => import('./pages/production/WorkOrders'));
const WorkCenters = lazy(() => import('./pages/production/WorkCenters'));
const Routings = lazy(() => import('./pages/production/Routings'));
const ProductionReports = lazy(() => import('./pages/production/ProductionReports'));
const MaterialIssues = lazy(() => import('./pages/production/MaterialIssues'));
const ScrapEntries = lazy(() => import('./pages/production/ScrapEntries'));
const Downtime = lazy(() => import('./pages/production/Downtime'));
const ApprovalRules = lazy(() => import('./pages/settings/approvals/ApprovalRules'));
const ApprovalRequests = lazy(() => import('./pages/approvals/ApprovalRequests'));
const ImportGRNs = lazy(() => import('./pages/imports/ImportGRNs'));
const TradingMargin = lazy(() => import('./pages/imports/TradingMargin'));
const PayrollRuns = lazy(() => import('./pages/hr/PayrollRuns'));
const Payslips = lazy(() => import('./pages/hr/Payslips'));
const PayrollReports = lazy(() => import('./pages/hr/PayrollReports'));
const StoreIssuancePage = lazy(() => import('./pages/purchases/requisitions/StoreIssuancePage'));
// Import Payment module components
const PaymentDashboard = lazy(() => import('./pages/payments/PaymentDashboard'));
const VendorPaymentForm = lazy(() => import('./pages/payments/VendorPaymentForm'));
const CustomerPaymentForm = lazy(() => import('./pages/payments/CustomerPaymentForm'));
const PaymentView = lazy(() => import('./pages/payments/PaymentView'));
// Import additional components for the landing page sections
const Intro = lazy(() => import('./Components/HomePage/Intro/Intro'));
const Services = lazy(() => import('./Components/HomePage/Services/Services'));
const About = lazy(() => import('./Components/HomePage/About/About'));
const Overview = lazy(() => import('./Components/HomePage/Overview/Overview'));
const Process = lazy(() => import('./Components/HomePage/Process/Process'));
const Features = lazy(() => import('./Components/HomePage/Features/Features'));
// Utility function to get user subscription plan from localStorage
function getUserSubscriptionPlan() {
  try {
    return localStorage.getItem("user.subscriptionPlan");
  } catch {
    return null;
  }
}

const userType = getUserSubscriptionPlan();
console.log(userType);

// Route error boundary — captures loader/action errors and logs them
const ErrorBoundary = () => {
  const error = useRouteError();
  console.error("[Route Error]", error);
  const message =
    error?.message || error?.statusText || "An unexpected error occurred.";
  return (
    <div className="error-container" style={{ padding: "2rem", textAlign: "center" }}>
      <h2>Oops! Something went wrong</h2>
      <p style={{ color: "#666", marginBottom: "1rem" }}>{message}</p>
      <button onClick={() => (window.location.href = "/login")}>
        Back to Login
      </button>
    </div>
  );
};

const LayoutWithSuspense = () => (
  <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>}>
    <Layout />
  </Suspense>
);

const Router = createBrowserRouter([
  // Public Routes
  {
    path: "/",
    element: <LandingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/login",
    element: <Login />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/maintenance",
    element: (
      <Suspense fallback={<div />}>
        <Maintenance />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/change-password",
    element: <ChangePassword />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/super-admin-login",
    element: <SuperAdminLogin />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/super-admin",
    element: (
      <SAProtectedRoute>
        <Suspense fallback={<div />}>
          <SuperAdmin />
        </Suspense>
      </SAProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/pricing",
    element: <PricingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/privacy",
    element: <Privacy />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/refund",
    element: <Refund />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/terms",
    element: <Terms />,
    errorElement: <ErrorBoundary />,
  },
  // Protected Application Routes
  {
    element: (
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <LayoutWithSuspense />,
        children: [
          // ✅ INDEX & MAIN DASHBOARD ROUTES
          {
            path: "/dashboard",
            element: (
              <PlanProtectedRoute
                requiredFeature="dashboard"
                fallbackPath="/customers"
              >
                <Home />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "/admin/custom-roles",
            element: <CustomRoles />,
          },
          {
            path: "/accounts",
            element: <ChartOfAccounts />,
          },
          {
            path: "/accounts/journal",
            element: <JournalVouchers />,
          },
          {
            path: "/accounts/financial",
            element: <FinancialAccounts />,
          },
          {
            path: "/accounts/fiscal-years",
            element: <FiscalYears />,
          },
          {
            path: "/accounts/cash-book",
            element: <CashBook />,
          },
          {
            path: "/accounts/reconciliation",
            element: <BankReconciliation />,
          },
          {
            path: "/accounts/control-center",
            element: <ControlCenter />,
          },
          {
            path: "/accounts/petty-cash",
            element: <PettyCash />,
          },
          {
            path: "/customers/ledger",
            element: <CustomerLedger />,
          },
          {
            path: "/vendors/ledger",
            element: <VendorLedger />,
          },
          {
            path: "/collections",
            element: <Collections />,
          },
          {
            path: "/expense-bills",
            element: <ExpenseBills />,
          },
          {
            path: "/reports/ap-reconciliation",
            element: <ApReconciliation />,
          },
          {
            path: "/settings/withholding-taxes",
            element: <WithholdingTaxes />,
          },
          {
            path: "/settings/tax-codes",
            element: <TaxCodes />,
          },
          {
            path: "/hr/operations",
            element: <HrOperations />,
          },
          {
            path: "/hr/advances/new",
            element: <AdvanceEditor />,
          },
          {
            path: "/hr/advances/:id/edit",
            element: <AdvanceEditor />,
          },
          {
            path: "/hr/settlements/new",
            element: <SettlementEditor />,
          },
          {
            path: "/hr/settlements/:id/edit",
            element: <SettlementEditor />,
          },
          {
            path: "/assets/lifecycle",
            element: <AssetLifecycle />,
          },
          {
            path: "/production/mrp",
            element: <MrpRuns />,
          },
          {
            path: "/production/quality",
            element: <QualityChecks />,
          },
          {
            path: "/admin/audit-logs",
            element: <AuditLogsPage />,
          },
          {
            path: "/admin/security",
            element: <SecurityCenter />,
          },
          {
            path: "/accounts/document-lifecycle",
            element: <DocumentLifecycle />,
          },
          {
            path: "/accounts/credit-notes",
            element: <CreditNotes />,
          },
          {
            path: "/accounts/bank-transfer",
            element: <BankTransfer />,
          },
          {
            path: "/assets/reconciliation",
            element: <FaReconciliation />,
          },
          {
            path: "/accounts/period-close",
            element: <PeriodCloseWizard />,
          },
          {
            path: "/reports/professional/trial-balance",
            element: <TrialBalanceView />,
          },
          {
            path: "/reports/professional/profit-loss",
            element: <ProfitLossView />,
          },
          {
            path: "/reports/professional/balance-sheet",
            element: <BalanceSheetView />,
          },
          {
            path: "/reports/professional/cashflow",
            element: <CashflowView />,
          },
          {
            path: "/reports/professional/customer-aging",
            element: <CustomerAgingView />,
          },
          {
            path: "/reports/professional/aged-creditors",
            element: <AgedCreditorsView />,
          },
          {
            path: "/reports/professional/vat",
            element: <VatView />,
          },
          {
            path: "/reports/professional/corporate-tax",
            element: <CorporateTaxView />,
          },
          {
            path: "/reports/professional/payroll-by-department",
            element: <PayrollByDepartmentView />,
          },
          {
            path: "/reports/professional/expense-by-category",
            element: <ExpenseByCategoryView />,
          },
          {
            path: "/assets/tagging",
            element: <AssetTagging />,
          },
          {
            path: "/production/variance",
            element: <ProductionVariance />,
          },
          {
            path: "/hr/payslip",
            element: <Payslip />,
          },
          {
            path: "/hr/payslip/:runId",
            element: <Payslip />,
          },
          {
            path: "/hr/payslip/:runId/:employeeId",
            element: <Payslip />,
          },
          {
            path: "/settings/2fa",
            element: <TwoFactorSetup />,
          },
          {
            path: "/inventory/valuation-fifo",
            element: <InventoryFifoValuation />,
          },
          {
            path: "/settings/payroll-tax",
            element: <PayrollTaxSlabs />,
          },
          {
            path: "/settings/exchange-rates",
            element: <ExchangeRates />,
          },
          {
            path: "/settings/posting-rules",
            element: <PostingRules />,
          },
          {
            path: "/settings/cost-centers",
            element: <CostCenters />,
          },
          {
            path: "/assets",
            element: <FixedAssets />,
          },
          {
            path: "/assets/depreciation",
            element: <Depreciation />,
          },
          {
            path: "/settings/jv-numbering",
            element: <JVNumberingRules />,
          },
          {
            path: "/expense-reimbursements",
            element: <ExpenseReimbursements />,
          },
          {
            path: "/inter-company",
            element: <InterCompanyTransactions />,
          },
          {
            path: "/accruals",
            element: <Accruals />,
          },
          {
            path: "/hr/employees",
            element: <Employees />,
          },
          {
            path: "/hr/payroll",
            element: <PayrollRuns />,
          },
          {
            path: "/hr/payslips",
            element: <Payslips />,
          },
          {
            path: "/hr/payroll-reports",
            element: <PayrollReports />,
          },
          {
            path: "/imports/letters-of-credit",
            element: <LettersOfCredit />,
          },
          {
            path: "/imports/shipments",
            element: <Shipments />,
          },
          {
            path: "/imports/customs",
            element: <CustomsEntries />,
          },
          {
            path: "/imports/landed-costs",
            element: <LandedCosts />,
          },
          {
            path: "/imports/lc-settlements",
            element: <LcSettlements />,
          },
          {
            path: "/imports/cost-analytics",
            element: <ImportCostAnalytics />,
          },
          {
            path: "/customers",
            element: <Customers />,
          },
          {
            path: "/vendors",
            element: <Vendors />,
          },
          {
            path: "/profile-settings",
            element: <ProfileSettings />,
          },
          {
            path: "/support",
            element: <SupportTickets />,
          },
          {
            path: "/support/:id",
            element: <SupportTickets />,
          },
          {
            path: "/messages",
            element: <Messages />,
          },
          {
            path: "/messages/:threadId",
            element: <Messages />,
          },
          {
            path: "/app-settings",
            element: <AppSettings />,
          },
          {
            path: "/invoice/:invoiceId/payment",
            element: <RecordPayment />,
          },
          {
            path: "/invoice/:id",
            element: <ShowInvoice />,
          },
          {
            path: "/payment/:paymentId/show",
            element: <ShowPayment />,
          },
          {
            path: "/payment/:paymentId/edit",
            element: <RecordPayment isEditing={true} />,
          },
          {
            path: "/reports/ledger",
            element: (
              <PlanProtectedRoute requiredFeature="basicLedger">
                <Ledger />
              </PlanProtectedRoute>
            ),
          },

          // Legacy payment route redirect
          {
            path: "/payment",
            element: <Navigate to="/payments" replace />,
          },

          // Sales Module Routes
          {
            path: "/quotes",
            element: <QuotesList />,
          },
          {
            path: "/quotes/new",
            element: <QuoteForm />,
          },
          {
            path: "quotes/:id/edit",
            element: <QuoteForm />,
          },
          {
            path: "quotes/:id",
            element: <QuoteForm />,
          },
          {
            path: "sales/invoices",
            element: <InvoicesList />,
          },
          {
            path: "sales/invoices/new",
            element: <InvoiceForm key="new-invoice-form" />,
          },
          {
            path: "sales/invoices/:id/edit",
            element: <InvoiceForm key="edit-sales-invoice-form" />,
          },
          {
            path: "sales/invoices/:id",
            element: <InvoiceForm />,
          },
          {
            path: "sales/invoices/:id/show",
            element: <ShowSaleInvoice />,
          },

          // Add routes for the new Sales components
          {
            path: "sales/orders",
            element: <OrdersList />,
          },
          {
            path: "sales/orders/new",
            element: <OrderForm />,
          },
          {
            path: "sales/orders/:id",
            element: <OrderForm />,
          },
          {
            path: "sales/orders/:id/edit",
            element: <OrderForm />,
          },
          // /sales/credits removed — see /accounts/credit-notes.
          {
            path: "sales/secondary",
            element: <SecondarySales />,
          },
          {
            path: "sales/distributor-stock",
            element: <DistributorStock />,
          },

          // Sales Dashboard - Team Plan Required (charts / trends / top customers)
          {
            path: "sales/dashboard",
            element: (
              <PlanProtectedRoute requiredFeature="dashboard">
                <SalesDashboard />
              </PlanProtectedRoute>
            ),
          },

          

          // Purchases Module Routes
          // Purchase Dashboard - Pro Plan Required (both paths for compatibility)
          {
            path: "purchases",
            element: (
              <PlanProtectedRoute requiredFeature="dashboard">
                <PurchaseDashboard />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "purchases/dashboard",
            element: (
              <PlanProtectedRoute requiredFeature="dashboard">
                <PurchaseDashboard />
              </PlanProtectedRoute>
            ),
          },

          // Requisitions Routes
          {
            path: "purchases/requisitions",
            element: <RequisitionsList />,
          },
          {
            path: "purchases/requisitions/new",
            element: <RequisitionForm />,
          },
          {
            path: "purchases/requisitions/:id",
            element: <RequisitionForm />,
          },
          {
            path: "purchases/requisitions/:id/edit",
            element: <RequisitionForm />,
          },
          {
            path: "purchases/requisitions/approvals",
            element: <ApprovalPage />,
          },
          {
            path: "purchases/requisitions/issuance",
            element: <StoreIssuancePage />,
          },

          // Vendor Quotes Routes
          {
            path: "purchases/quotes",
            element: <VendorQuotesList />,
          },
          {
            path: "purchases/quotes/new",
            element: <VendorQuoteForm />,
          },
          {
            path: "purchases/quotes/:id",
            element: <VendorQuoteForm />,
          },
          {
            path: "purchases/quotes/:id/edit",
            element: <VendorQuoteForm />,
          },

          // Purchase Orders (PO) Routes
          {
            path: "purchases/po",
            element: <POList />,
          },
          {
            path: "purchases/po/new",
            element: <POForm />,
          },
          {
            path: "purchases/po/:id",
            element: <POForm />,
          },
          {
            path: "purchases/po/:id/edit",
            element: <POForm />,
          },

          // GRN Routes
          {
            path: "purchases/grn",
            element: <GRNList />,
          },
          {
            path: "purchases/grn/new",
            element: <GRNForm />,
          },
          {
            path: "purchases/grn/:id",
            element: <GRNForm />,
          },

          // Purchase Orders Routes
          {
            path: "purchases/orders",
            element: <PurchaseOrdersList />,
          },
          {
            path: "purchases/orders/new",
            element: <PurchaseOrderForm />,
          },
          {
            path: "purchases/orders/:id",
            element: <PurchaseOrderForm />,
          },
          {
            path: "purchases/orders/:id/edit",
            element: <PurchaseOrderForm />,
          },

          // Bills Routes
          {
            path: "purchases/bills",
            element: <PurchaseBillsList />,
          },
          {
            path: "purchases/bills/new",
            element: <PurchaseBillForm />,
          },
          {
            path: "purchases/bills/:id",
            element: <PurchaseBillForm />,
          },
          {
            path: "purchases/bills/:id/edit",
            element: <PurchaseBillForm />,
          },

          // Returns Routes
          {
            path: "purchases/returns",
            element: <PurchaseReturnsList />,
          },
          {
            path: "purchases/returns/new",
            element: <PurchaseReturnForm />,
          },
          {
            path: "purchases/returns/:id",
            element: <PurchaseReturnForm />,
          },
          {
            path: "purchases/returns/:id/edit",
            element: <PurchaseReturnForm />,
          },

          // Reports Route - Pro Plan Required
          {
            path: "purchases/reports",
            element: (
              <PlanProtectedRoute requiredFeature="analytics">
                <PurchasesReports />
              </PlanProtectedRoute>
            ),
          },

          // Analytics Route - Pro Plan Required
          {
            path: "purchases/analytics",
            element: (
              <PlanProtectedRoute requiredFeature="analytics">
                <PurchaseAnalytics />
              </PlanProtectedRoute>
            ),
          },

          // Product Module Routes
          {
            path: "products",
            element: <InventoryList />,
          },
          {
            path: "products/new",
            element: <InventoryForm />,
          },
          {
            path: "products/:id/edit",
            element: <InventoryForm />,
          },

          // Service Module Routes
          {
            path: "services",
            element: <ServiceList />,
          },
          {
            path: "services/new",
            element: <ServiceForm />,
          },
          {
            path: "services/:id/edit",
            element: <ServiceForm />,
          },

          // Inventory Module Routes
          {
            path: "inventory",
            element: (
              <PlanProtectedRoute requiredFeature="dashboard">
                <InventoryDashboard />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "inventory/dashboard",
            element: (
              <PlanProtectedRoute requiredFeature="dashboard">
                <InventoryDashboard />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "inventory/list",
            element: <InventoryList />,
          },
          {
            path: "inventory/new",
            element: <InventoryForm />,
          },
          {
            path: "inventory/show/:id",
            element: <ShowInventory />,
          },
          {
            path: "inventory/:id/edit",
            element: <InventoryForm />,
          },

          // Adjustments Routes (both paths for compatibility)
          {
            path: "inventory/adjustments",
            element: <InventoryAdjustments />,
          },
          {
            path: "inventory/adjustments/new",
            element: <InventoryAdjustmentForm />,
          },
          {
            path: "inventory/adjustments/:id/edit",
            element: <InventoryAdjustmentForm />,
          },
          {
            path: "inventory/transfers",
            element: <InventoryTransfers />,
          },
          {
            path: "inventory/transfers/new",
            element: <InventoryTransferForm />,
          },
          {
            path: "inventory/transfers/:id/edit",
            element: <InventoryTransferForm />,
          },
          // Inventory Valuation - root-level route removed; use inventory/valuations only
          // {
          //   path: "valuations",
          //   element: (
          //     <PlanProtectedRoute requiredFeature="inventoryValuation">
          //       <InventoryValuation />
          //     </PlanProtectedRoute>
          //   ),
          // },
          {
            path: "inventory/valuations",
            element: (
              <PlanProtectedRoute requiredFeature="inventoryValuation">
                <InventoryValuation />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "inventory/reports",
            element: (
              <PlanProtectedRoute requiredFeature="analytics">
                <InventoryReports />
              </PlanProtectedRoute>
            ),
          },

          // Category Module Routes
          {
            path: "categories",
            element: <CategoryList />,
          },
          {
            path: "categories/new",
            element: <CategoryForm />,
          },
          {
            path: "categories/:id/edit",
            element: <CategoryForm />,
          },

          // Unit of Measure Routes
          {
            path: "settings/units",
            element: <UnitOfMeasureList />,
          },
          {
            path: "settings/units/new",
            element: <UnitOfMeasureForm />,
          },
          {
            path: "settings/units/:id/edit",
            element: <UnitOfMeasureForm />,
          },

          // Reports Module Routes - Pro Plan Required
          {
            path: "reports",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <ReportsDashboard />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/profit-loss",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <ProfitLossReport />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/balance-sheet",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <BalanceSheet />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/chart-of-accounts",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <ChartOfAccountsReport />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/trial-balance",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <TrialBalance />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/postings",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <Postings />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/cashflow",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <Cashflow />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/tax",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <TaxReport />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/corporate-tax",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <CorporateTax />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/vat",
            element: (
              <PlanProtectedRoute requiredFeature="advancedReports">
                <VAT />
              </PlanProtectedRoute>
            ),
          },
          // Customer Aging - Pro Plan Required
          {
            path: "reports/customers/aging",
            element: (
              <PlanProtectedRoute requiredFeature="customerAging">
                <CustomerAgingReport />
              </PlanProtectedRoute>
            ),
          },
          // Vendor Aging - Pro Plan Required
          {
            path: "reports/vendors/aging",
            element: (
              <PlanProtectedRoute requiredFeature="customerAging">
                <AgedCreditors />
              </PlanProtectedRoute>
            ),
          },
          // Existing module reports - Pro Plan Required
          {
            path: "reports/sales",
            element: (
              <PlanProtectedRoute requiredFeature="analytics">
                <SalesReports />
              </PlanProtectedRoute>
            ),
          },
          {
            path: "reports/purchases",
            element: (
              <PlanProtectedRoute requiredFeature="analytics">
                <PurchasesReports />
              </PlanProtectedRoute>
            ),
          },

          // Admin Module Routes
          {
            path: "admin/users",
            element: <UserManagement />,
          },
          {
            path: "admin/user-permissions",
            element: <UserPermissions />,
          },
          {
            path: "admin/branches",
            element: <BranchManagement />,
          },
          {
            path: "admin/departments",
            element: <DepartmentManagement />,
          },
          {
            path: "admin/authority",
            element: <AuthorityManagement />,
          },
          {
            path: "admin/roles",
            element: <RoleManagement />,
          },
          {
            path: "admin/user-roles",
            element: <UserRoles />,
          },
          // {
          //   path: "admin/user-registration",
          //   element: <UserRegistration />,
          // },
          {
            path: "admin/status-dashboard",
            element: <StatusDashboard />,
          },

          // Payments Module Routes
          {
            path: "payments",
            element: <PaymentDashboard />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: "payments/vendor/new",
            element: <VendorPaymentForm />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: "payments/vendor/:id/edit",
            element: <VendorPaymentForm />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: "payments/customer/new",
            element: <CustomerPaymentForm />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: "payments/customer/:id/edit",
            element: <CustomerPaymentForm />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: "payments/:id",
            element: <PaymentView />,
            errorElement: <ErrorBoundary />,
          },

          // Finance Dashboard Route - Team Plan Required
          {
            path: "finance/dashboard",
            element: (
              <PlanProtectedRoute requiredFeature="dashboard">
                <FinanceDashboard />
              </PlanProtectedRoute>
            ),
          },

          // Production Module Routes
          {
            path: "production/boms",
            element: <BillOfMaterials />,
          },
          {
            path: "production/work-orders",
            element: <WorkOrders />,
          },
          {
            path: "production/work-centers",
            element: <WorkCenters />,
          },
          {
            path: "production/routings",
            element: <Routings />,
          },
          {
            path: "production/material-issues",
            element: <MaterialIssues />,
          },
          {
            path: "production/reports",
            element: <ProductionReports />,
          },
          {
            path: "production/scrap",
            element: <ScrapEntries />,
          },
          {
            path: "production/downtime",
            element: <Downtime />,
          },
          {
            path: "/settings/approval-rules",
            element: <ApprovalRules />,
          },
          {
            path: "/approvals",
            element: <ApprovalRequests />,
          },
          {
            path: "/imports/grns",
            element: <ImportGRNs />,
          },
          {
            path: "/imports/trading-margin",
            element: <TradingMargin />,
          },

          // User Guide Route
          {
            path: "guide",
            element: <UserGuide />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: "setup/opening-balances",
            element: <OpeningBalances />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
    ],
  },
]);

export default Router;
